/*!
 * te-forms.js: progressive enhancement for totalElement website forms.
 * Works with hand-written markup (see snippets/te-contact-form.liquid) or renders a form from the
 * backend schema: <form data-te-form="product-question" data-te-render data-endpoint="/apps/te-forms/forms/product-question"></form>
 *
 * Markup conventions
 *   form[data-te-form=id]           enables the script; data-endpoint (default: action, then /apps/te-forms/forms/id)
 *   data-files-endpoint             optional (Cloudflare Worker): each attached file is uploaded here first, e.g.
 *                                   https://te-forms.example.workers.dev/forms/contact/files, and the form is then
 *                                   sent with a short token per file instead of the file itself
 *   data-upload-endpoint            optional (Node server): where to post the whole form when files are attached
 *   [data-show-when="topic=bulk,custom"]   show only when the field has one of the values ("a=1;b=2" means both)
 *   [data-required-when="topic=order"]     on a control: required only when it matches
 *   [data-te-success]               element shown after sending; [data-te-ref] and [data-te-message] are filled in
 *   [data-te-status]                live region for form-level messages (created if missing)
 *   data-ga-start / data-ga-topic / data-lead-source   GA4 events (gtag or dataLayer, if present)
 *   data-fallback-email             shown when sending fails (default help@totalelement.com)
 */
(function () {
  'use strict';
  if (window.TeForms) return;

  var MB = 1024 * 1024;
  var MIN_FILL_MS = 2100; // the server tags messages sent sooner than 2 s after the form loaded (bots)
  var uid = 0;
  var lc = function (s) { return s ? s.charAt(0).toLowerCase() + s.slice(1) : s; };
  var fmtBytes = function (n) { return n >= MB ? (n / MB).toFixed(n >= 10 * MB ? 0 : 1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB'; };
  var newSid = function () {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'x' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  };

  function track(name, params) {
    if (!name) return;
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) { /* analytics must never break the form */ }
  }

  function hiddenInput(form, name, value) {
    var el = form.querySelector('input[type=hidden][name="' + name + '"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    if (value !== undefined) el.value = value;
    return el;
  }

  function honeypot(form) {
    if (form.querySelector('[name=_hp]')) return;
    var wrap = document.createElement('div');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = 'position:absolute!important;left:-10000px!important;width:1px;height:1px;overflow:hidden';
    wrap.innerHTML = '<label>Leave this field empty <input type="text" name="_hp" tabindex="-1" autocomplete="off"></label>';
    form.appendChild(wrap);
  }

  // Current value of a named field, ignoring controls hidden by a condition.
  function valueOf(form, name) {
    var els = form.querySelectorAll('[name="' + name + '"]');
    var out = [];
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.disabled && el.hasAttribute('data-te-off')) continue;
      if (el.type === 'radio' || el.type === 'checkbox') { if (el.checked) out.push(el.value || 'on'); }
      else if (el.value) out.push(el.value);
    }
    return out;
  }

  function matches(form, spec) {
    return String(spec).split(';').every(function (part) {
      var eq = part.indexOf('=');
      var name = (eq < 0 ? part : part.slice(0, eq)).trim();
      var wanted = eq < 0 ? null : part.slice(eq + 1).split(',').map(function (s) { return s.trim(); });
      var have = valueOf(form, name);
      return wanted ? have.some(function (v) { return wanted.indexOf(v) >= 0; }) : have.length > 0;
    });
  }

  function applyConditions(form) {
    for (var pass = 0; pass < 3; pass++) {
      form.querySelectorAll('[data-show-when]').forEach(function (el) {
        var show = matches(form, el.getAttribute('data-show-when'));
        el.hidden = !show;
        el.querySelectorAll('input,select,textarea').forEach(function (c) {
          if (!show && !c.disabled) { c.disabled = true; c.setAttribute('data-te-off', ''); }
          if (show && c.hasAttribute('data-te-off')) { c.disabled = false; c.removeAttribute('data-te-off'); }
        });
      });
    }
    form.querySelectorAll('[data-required-when]').forEach(function (c) {
      c.required = matches(form, c.getAttribute('data-required-when'));
    });
    // Labels that change with the topic: <span data-te-alt="topic=custom">Project notes</span>;
    // the first matching alternative shows, otherwise the [data-te-default] text.
    form.querySelectorAll('[data-te-label]').forEach(function (host) {
      var found = false;
      host.querySelectorAll('[data-te-alt]').forEach(function (s) {
        var on = !found && matches(form, s.getAttribute('data-te-alt'));
        s.hidden = !on;
        if (on) found = true;
      });
      var def = host.querySelector('[data-te-default]');
      if (def) def.hidden = found;
    });
    // "(optional)" markers follow whether the field's control is currently required.
    form.querySelectorAll('[data-te-opt]').forEach(function (m) {
      var field = m.closest('[data-field]');
      var c = field && field.querySelector('input:not([type=hidden]),select,textarea');
      m.hidden = Boolean(c && c.required);
    });
  }

  function labelFor(control) {
    var lab = control.id && control.form.querySelector('label[for="' + control.id + '"]');
    if (!lab) {
      var fs = control.closest('fieldset');
      lab = fs && fs.querySelector('legend');
    }
    if (!lab) return control.name;
    var text = '';
    lab.childNodes.forEach(function (n) {
      if (n.nodeType === 3) text += n.textContent;
      else if (n.nodeType === 1 && !n.hidden && n.tagName !== 'SMALL' && n.tagName !== 'INPUT') text += n.textContent;
    });
    return text.replace(/\s+/g, ' ').trim();
  }

  function fileProblem(input) {
    var max = Number(input.getAttribute('data-max-files') || 5);
    var per = Number(input.getAttribute('data-max-file-size') || 20 * MB);
    var total = Number(input.getAttribute('data-max-total-size') || 50 * MB);
    var exts = (input.getAttribute('accept') || '').toLowerCase().split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var files = Array.prototype.slice.call(input.files || []);
    if (files.length > max) return 'Attach up to ' + max + ' files.';
    var sum = 0;
    for (var i = 0; i < files.length; i++) {
      var f = files[i];
      var ext = (/\.[a-z0-9]+$/i.exec(f.name) || [''])[0].toLowerCase();
      if (exts.length && exts.indexOf(ext) < 0) return '“' + f.name + '” isn’t a file type we accept here.';
      if (f.size > per) return '“' + f.name + '” is ' + fmtBytes(f.size) + '. The limit is ' + fmtBytes(per) + ' per file.';
      sum += f.size;
    }
    if (sum > total) return 'These files add up to more than ' + fmtBytes(total) + '. Send fewer or smaller files.';
    return '';
  }

  function messageFor(c) {
    var v = c.validity;
    var label = labelFor(c);
    if (v.valueMissing) {
      if (c.getAttribute('data-msg-required')) return c.getAttribute('data-msg-required');
      if (c.type === 'checkbox') return 'Check this box to continue.';
      if (c.type === 'file') return 'Attach at least one file.';
      if (c.type === 'radio' || c.tagName === 'SELECT') return /\?$/.test(label) ? 'Choose an answer.' : 'Choose ' + lc(label) + '.';
      if (/\?$/.test(label)) return 'Please answer this question.';
      if (/^(tell|describe|list|explain|share|add|include|give|enter|let)\b/i.test(label)) return 'Please fill this in.';
      return 'Enter ' + lc(label) + '.';
    }
    if (v.typeMismatch && c.type === 'email') return 'Enter an email address like name@example.com.';
    if (v.typeMismatch) return 'Check the format of this field.';
    if (v.tooLong) return 'Keep this under ' + c.maxLength + ' characters.';
    if (v.patternMismatch) return c.getAttribute('data-msg-pattern') || 'Check the format of this field.';
    if (c.type === 'file') return fileProblem(c);
    var min = c.getAttribute('data-min');
    if (min !== null && c.value) {
      var n = Number(c.value.replace(/[\s,_]/g, ''));
      if (!isFinite(n)) return 'Enter a number.';
      if (c.hasAttribute('data-integer') && Math.floor(n) !== n) return 'Enter a whole number.';
      if (n < Number(min)) return c.getAttribute('data-msg-min') || 'Enter a number of ' + min + ' or more.';
    }
    return '';
  }

  function errorSlot(form, name) {
    var slot = form.querySelector('[data-error-for="' + name + '"]');
    if (slot) return slot;
    var c = form.querySelector('[name="' + name + '"]');
    if (!c) return null;
    var host = c.closest('[data-field]') || c.closest('.te-field') || c.parentNode;
    slot = document.createElement('p');
    slot.className = 'te-err';
    slot.setAttribute('data-error-for', name);
    slot.id = 'tef' + (++uid) + '-err';
    slot.hidden = true;
    host.appendChild(slot);
    return slot;
  }

  function setError(form, name, msg) {
    var slot = errorSlot(form, name);
    var controls = form.querySelectorAll('[name="' + name + '"]');
    controls.forEach(function (c) {
      if (msg) c.setAttribute('aria-invalid', 'true'); else c.removeAttribute('aria-invalid');
      var ids = (c.getAttribute('aria-describedby') || '').split(' ').filter(Boolean).filter(function (x) { return !slot || x !== slot.id; });
      if (msg && slot) ids.push(slot.id);
      if (ids.length) c.setAttribute('aria-describedby', ids.join(' ')); else c.removeAttribute('aria-describedby');
    });
    var host = controls[0] && (controls[0].closest('[data-field]') || controls[0].closest('.te-field'));
    if (host) host.classList.toggle('is-invalid', Boolean(msg));
    if (slot) { slot.textContent = msg || ''; slot.hidden = !msg; }
  }

  // Once every field problem is fixed, drop the form-level "please check" message too.
  function settleStatus(form) {
    var st = form.querySelector('[data-te-status].te-status--error');
    if (st && !form.querySelector('[data-error-for]:not([hidden])')) setStatus(form, '', '');
  }

  function clearErrors(form) {
    form.querySelectorAll('[data-error-for]').forEach(function (s) { setError(form, s.getAttribute('data-error-for'), ''); });
  }

  function validate(form) {
    var errors = {};
    form.querySelectorAll('input,select,textarea').forEach(function (c) {
      if (c.disabled || !c.name || c.name.charAt(0) === '_' || c.type === 'hidden' || errors[c.name]) return;
      var msg = messageFor(c);
      if (msg) errors[c.name] = msg;
    });
    return errors;
  }

  function showErrors(form, errors) {
    var names = Object.keys(errors);
    names.forEach(function (n) { setError(form, n, errors[n]); });
    var first = names.length && form.querySelector('[name="' + names[0] + '"]:not([disabled])');
    if (first) first.focus();
  }

  function statusRegion(form) {
    var el = form.querySelector('[data-te-status]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-te-status', '');
      el.className = 'te-status';
      var btn = form.querySelector('[type=submit]');
      (btn && btn.parentNode ? btn.parentNode : form).appendChild(el);
    }
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    return el;
  }

  function setStatus(form, text, kind) {
    var el = statusRegion(form);
    el.textContent = text || '';
    el.className = 'te-status' + (kind ? ' te-status--' + kind : '');
  }

  function renderFileList(input) {
    var host = input.closest('[data-field]') || input.parentNode;
    var list = host.querySelector('.te-files');
    if (!list) { list = document.createElement('ul'); list.className = 'te-files'; host.appendChild(list); }
    list.innerHTML = '';
    Array.prototype.forEach.call(input.files || [], function (f) {
      var li = document.createElement('li');
      li.textContent = f.name + ' (' + fmtBytes(f.size) + ')';
      list.appendChild(li);
    });
    setError(input.form, input.name, fileProblem(input));
  }

  function send(url, body, onProgress, headers) {
    return new Promise(function (resolve) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Accept', 'application/json');
      Object.keys(headers || {}).forEach(function (k) { xhr.setRequestHeader(k, headers[k]); });
      xhr.timeout = 150000;
      if (onProgress && xhr.upload) xhr.upload.onprogress = function (e) { if (e.lengthComputable) onProgress(e.loaded / e.total); };
      xhr.onload = function () {
        var parsed = null;
        try { parsed = JSON.parse(xhr.responseText); } catch (e) { /* not JSON */ }
        resolve({ status: xhr.status, body: parsed || {} });
      };
      xhr.onerror = xhr.ontimeout = function () { resolve({ status: 0, body: {} }); };
      xhr.send(body);
    });
  }

  // The form's text answers, URL-encoded (small, and safe through the Shopify App Proxy).
  function fieldsOf(form) {
    var out = new URLSearchParams();
    new FormData(form).forEach(function (v, k) { if (typeof v === 'string') out.append(k, v); });
    return out;
  }

  // ---- Direct uploads (data-files-endpoint): files go to storage first, the form carries tokens ----
  // A token is reused if the same file is sent again (after fixing another field), so it uploads once.
  var tokens = typeof WeakMap === 'function' ? new WeakMap() : null;
  var TOKEN_REUSE_MS = 20 * 60 * 60 * 1000; // the server accepts a token for 24 hours

  function cachedToken(file, endpoint, field) {
    var c = tokens && tokens.get(file);
    return c && c.endpoint === endpoint && c.field === field && Date.now() - c.at < TOKEN_REUSE_MS ? c.token : null;
  }

  function forget(files) {
    if (tokens) files.forEach(function (f) { tokens.delete(f); });
  }

  // Resolves { tokens: [{field, token}], files } or { failed: response-like object }.
  function uploadFiles(inputs, endpoint, onProgress) {
    var jobs = [];
    inputs.forEach(function (input) {
      Array.prototype.forEach.call(input.files, function (file) { jobs.push({ field: input.name, file: file, token: cachedToken(file, endpoint, input.name) }); });
    });
    var weight = function (j) { return Math.max(1, j.file.size); };
    var total = jobs.reduce(function (n, j) { return n + (j.token ? 0 : weight(j)); }, 0);
    var done = 0;
    var i = 0;
    function next() {
      if (i >= jobs.length) return Promise.resolve({ tokens: jobs.map(function (j) { return { field: j.field, token: j.token }; }), files: jobs.map(function (j) { return j.file; }) });
      var j = jobs[i++];
      if (j.token) return next();
      var url = endpoint + (endpoint.indexOf('?') < 0 ? '?' : '&') + 'field=' + encodeURIComponent(j.field) + '&name=' + encodeURIComponent(j.file.name);
      var progress = function (p) { if (onProgress) onProgress((done + p * weight(j)) / total); };
      return send(url, j.file, progress, { 'Content-Type': j.file.type || 'application/octet-stream' }).then(function (r) {
        var b = r.body;
        if (r.status === 201 && b.ok && b.file && b.file.token) {
          j.token = b.file.token;
          done += weight(j);
          if (tokens) tokens.set(j.file, { token: j.token, at: Date.now(), endpoint: endpoint, field: j.field });
          return next();
        }
        // A problem with this file (type, size, content): show it under the file field.
        if ([400, 411, 413, 415, 422].indexOf(r.status) >= 0 && b.message) {
          var errors = {};
          errors[j.field] = b.message;
          return { failed: { status: 422, body: { ok: false, errors: errors } } };
        }
        return { failed: r };
      });
    }
    return next();
  }

  function init(form) {
    if (!form || form.__teForms) return;
    form.__teForms = true;
    var id = form.getAttribute('data-te-form');
    var endpoint = form.getAttribute('data-endpoint') || form.getAttribute('action') || '/apps/te-forms/forms/' + id;
    var uploadEndpoint = form.getAttribute('data-upload-endpoint');
    var filesEndpoint = form.getAttribute('data-files-endpoint');
    var fallback = form.getAttribute('data-fallback-email') || 'help@totalelement.com';
    var topicField = form.getAttribute('data-ga-topic');
    var started = Date.now();
    var touched = false;

    form.setAttribute('novalidate', '');
    hiddenInput(form, '_sid', newSid());
    hiddenInput(form, '_t', '');
    hiddenInput(form, '_page', location.href.split('#')[0]);
    honeypot(form);
    applyConditions(form);

    form.addEventListener('change', function (e) {
      applyConditions(form);
      var t = e.target;
      if (t.name && t.type !== 'file') setError(form, t.name, '');
      if (t.type === 'file') renderFileList(t);
      if (topicField && t.name === topicField) track('inquiry_type_select', { inquiry_type: t.value, ui: form.getAttribute('data-ga-topic-ui') || 'chip', form_id: id });
      settleStatus(form);
    });
    form.addEventListener('input', function (e) {
      if (!touched) {
        touched = true;
        var p = { form_id: id };
        if (topicField) p.inquiry_type = valueOf(form, topicField)[0] || '';
        track(form.getAttribute('data-ga-start'), p);
      }
      if (e.target.getAttribute('aria-invalid') && !messageFor(e.target)) setError(form, e.target.name, '');
      settleStatus(form);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;
      applyConditions(form);
      clearErrors(form);
      var errors = validate(form);
      if (Object.keys(errors).length) {
        showErrors(form, errors);
        setStatus(form, 'Please check the highlighted fields.', 'error');
        return;
      }
      var fileInputs = Array.prototype.filter.call(form.querySelectorAll('input[type=file]:not([disabled])'), function (i) { return i.files && i.files.length; });
      var hasFiles = fileInputs.length > 0;
      var buttons = form.querySelectorAll('[type=submit]');
      buttons.forEach(function (b) { b.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      setStatus(form, hasFiles ? 'Uploading your files…' : 'Sending…', 'busy');
      var progress = function (p) { setStatus(form, p < 1 ? 'Uploading your files… ' + Math.round(p * 100) + '%' : 'Almost done…', 'busy'); };
      // Sent within moments of the page loading (the browser restored what was typed): wait out the
      // minimum first, so the message isn't mistaken for a bot's.
      var ready = new Promise(function (resolve) { setTimeout(resolve, Math.max(0, MIN_FILL_MS - (Date.now() - started))); });
      var stamp = function () { form.querySelector('[name=_t]').value = String(Date.now() - started); };

      var sending;
      if (hasFiles && filesEndpoint) {
        sending = uploadFiles(fileInputs, filesEndpoint, progress).then(function (up) {
          if (up.failed) return up.failed;
          return ready.then(function () {
            setStatus(form, 'Sending…', 'busy');
            stamp();
            var fields = fieldsOf(form);
            up.tokens.forEach(function (t) { fields.append(t.field, t.token); });
            return send(endpoint, fields).then(function (r) { r.files = up.files; return r; });
          });
        });
      } else if (hasFiles) {
        // No upload endpoint: the files go inside the post (multipart).
        sending = ready.then(function () { stamp(); return send(uploadEndpoint || endpoint, new FormData(form), progress); });
      } else {
        sending = ready.then(function () { stamp(); return send(endpoint, fieldsOf(form)); });
      }

      sending.then(function (r) {
        form.removeAttribute('aria-busy');
        buttons.forEach(function (b) { b.disabled = false; });
        var body = r.body;
        if (r.status >= 200 && r.status < 300 && body.ok) {
          if (r.files) forget(r.files); // the server has used these uploads
          return success(form, body, hasFiles);
        }
        if (r.status === 422 && body.errors) {
          // If the server refused an uploaded file (expired, say), upload it fresh next time.
          fileInputs.forEach(function (input) { if (body.errors[input.name]) forget(Array.prototype.slice.call(input.files)); });
          try { if (window.turnstile) window.turnstile.reset(); } catch (err) { /* widget not on this form */ }
          showErrors(form, body.errors);
          // Problems with fields this page doesn't show (markup out of date) go in the status line.
          var unseen = Object.keys(body.errors).filter(function (n) { return !form.querySelector('[name="' + n + '"]:not([disabled])'); }).map(function (n) { return body.errors[n]; });
          setStatus(form, unseen.length ? unseen.join(' ') : body.message || 'Please check the highlighted fields.', 'error');
          return;
        }
        try { if (window.turnstile) window.turnstile.reset(); } catch (err) { /* widget not on this form */ }
        var msg = body.message || (r.status === 0
          ? 'We couldn’t reach our server. Check your connection and try again, or email ' + fallback + '.'
          : 'We couldn’t send your message just now. Please try again in a few minutes, or email ' + fallback + '.');
        setStatus(form, msg, 'error');
      });
    });

    function success(form, body, hasFiles) {
      var params = { form_id: id, has_attachment: hasFiles, lead_source: form.getAttribute('data-lead-source') || id + '_form' };
      if (topicField) params.inquiry_type = valueOf(form, topicField)[0] || '';
      track('generate_lead', params);
      form.dispatchEvent(new CustomEvent('te-forms:success', { bubbles: true, detail: body }));
      var panel = form.querySelector('[data-te-success]') || (form.parentNode && form.parentNode.querySelector('[data-te-success="' + id + '"]'));
      if (panel) {
        var ref = panel.querySelector('[data-te-ref]');
        var msg = panel.querySelector('[data-te-message]');
        if (ref) ref.textContent = body.reference || '';
        if (msg && body.message) msg.textContent = body.message;
        Array.prototype.forEach.call(form.children, function (c) { if (c !== panel && !c.contains(panel)) c.hidden = true; });
        panel.hidden = false;
        panel.setAttribute('tabindex', '-1');
        panel.focus();
      } else {
        setStatus(form, (body.message || 'Thanks. Your message was sent.') + (body.reference ? ' Reference ' + body.reference + '.' : ''), 'ok');
        form.reset();
        form.querySelectorAll('.te-files').forEach(function (l) { l.innerHTML = ''; });
        applyConditions(form);
      }
      form.querySelector('[name=_sid]').value = newSid();
      started = Date.now();
    }
  }

  // ---- Optional: build the fields from the backend schema (GET endpoint) ----

  function simple(cond) {
    if (!cond || cond === true || cond === 'dynamic') return null;
    if (cond.field && cond['in']) return cond.field + '=' + cond['in'].join(',');
    if (cond.field && 'is' in cond) return cond.field + '=' + (cond.is === true ? 'on' : cond.is);
    if (cond.all) { var parts = cond.all.map(simple); return parts.every(Boolean) ? parts.join(';') : null; }
    return null;
  }

  function el(tag, attrs, text) {
    var n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) { if (attrs[k] !== undefined && attrs[k] !== null && attrs[k] !== false) n.setAttribute(k, attrs[k] === true ? '' : attrs[k]); });
    if (text) n.textContent = text;
    return n;
  }

  function labelNodes(host, f) {
    var alts = (f.labelWhen || []).map(function (a) { return { w: simple(a.when), label: a.label }; }).filter(function (a) { return a.w; });
    if (!alts.length) host.appendChild(document.createTextNode(f.label));
    else {
      host.setAttribute('data-te-label', '');
      host.appendChild(el('span', { 'data-te-default': '' }, f.label));
      alts.forEach(function (a) { host.appendChild(el('span', { 'data-te-alt': a.w, hidden: true }, a.label)); });
    }
    if (f.required !== true && f.type !== 'checkbox') host.appendChild(el('small', { 'data-te-opt': '' }, ' (optional)'));
  }

  function renderField(form, f, prefill) {
    var fid = 'tef' + (++uid) + '-' + f.name;
    if (f.type === 'hidden') return el('input', { type: 'hidden', name: f.name, value: prefill != null ? prefill : f['default'] || '' });
    var wrap = el('div', { 'class': 'te-field', 'data-field': f.name, 'data-show-when': simple(f.when) });
    var reqWhen = f.required && f.required !== true ? simple(f.required) : null;
    var common = { name: f.name, required: f.required === true, 'data-required-when': reqWhen, 'aria-describedby': f.help ? fid + '-help' : null };
    if (f.messages && f.messages.required) common['data-msg-required'] = f.messages.required;
    var control;
    if (f.type === 'radio' || f.type === 'checkboxes') {
      wrap = el('fieldset', { 'class': 'te-field te-fieldset', 'data-field': f.name, 'data-show-when': simple(f.when) });
      var legend = el('legend', { 'class': 'te-label' });
      labelNodes(legend, f);
      wrap.appendChild(legend);
      var group = el('div', { 'class': f.type === 'radio' ? 'te-chips' : 'te-cbs' });
      f.options.forEach(function (o, i) {
        var lab = el('label', { 'class': f.type === 'radio' ? 'te-chip' : 'te-cb' });
        var input = el('input', Object.assign({}, common, { type: f.type === 'radio' ? 'radio' : 'checkbox', id: fid + '-' + i, value: o.value, checked: f['default'] === o.value, required: f.type === 'radio' && f.required === true, 'data-required-when': f.type === 'radio' ? reqWhen : null }));
        lab.appendChild(input);
        lab.appendChild(el('span', {}, o.label));
        group.appendChild(lab);
      });
      wrap.appendChild(group);
    } else if (f.type === 'checkbox') {
      var cl = el('label', { 'class': 'te-cb' });
      cl.appendChild(el('input', Object.assign({}, common, { type: 'checkbox', id: fid })));
      cl.appendChild(el('span', {}, f.label));
      wrap.appendChild(cl);
    } else {
      var label = el('label', { 'class': 'te-label', 'for': fid });
      labelNodes(label, f);
      wrap.appendChild(label);
      if (f.type === 'select') {
        control = el('select', Object.assign({}, common, { id: fid, 'class': 'te-select' }));
        control.appendChild(el('option', { value: '' }, 'Choose one'));
        f.options.forEach(function (o) { control.appendChild(el('option', { value: o.value }, o.label)); });
      } else if (f.type === 'textarea') {
        control = el('textarea', Object.assign({}, common, { id: fid, 'class': 'te-textarea', rows: f.rows || 4, maxlength: f.maxLength }));
      } else if (f.type === 'file') {
        var drop = el('label', { 'class': 'te-drop', 'for': fid });
        control = el('input', Object.assign({}, common, { id: fid, type: 'file', multiple: f.maxFiles > 1, accept: f.acceptAttr, 'data-max-files': f.maxFiles, 'data-max-file-size': f.maxFileSize, 'data-max-total-size': f.maxTotalSize }));
        drop.appendChild(control);
        drop.appendChild(el('span', {}, 'Choose files or drop them here. Up to ' + fmtBytes(f.maxFileSize) + ' each.'));
        wrap.appendChild(drop);
        control = null;
      } else {
        var type = { email: 'email', tel: 'tel', url: 'url', date: 'date' }[f.type] || 'text';
        control = el('input', Object.assign({}, common, {
          id: fid, 'class': 'te-input', type: type, maxlength: f.maxLength, autocomplete: f.autocomplete || 'off',
          inputmode: f.type === 'number' ? 'numeric' : null, 'data-min': f.type === 'number' && f.min !== undefined ? f.min : null,
          'data-integer': f.type === 'number' && f.integer, value: prefill != null ? prefill : null,
        }));
      }
      if (control) wrap.appendChild(control);
    }
    if (f.help) wrap.appendChild(el('p', { 'class': 'te-help', id: fid + '-help' }, f.help));
    return wrap;
  }

  function render(form) {
    var endpoint = form.getAttribute('data-endpoint') || '/apps/te-forms/forms/' + form.getAttribute('data-te-form');
    return fetch(endpoint, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('schema ' + r.status); return r.json(); })
      .then(function (schema) {
        var frag = document.createDocumentFragment();
        schema.fields.forEach(function (f) { frag.appendChild(renderField(form, f, form.getAttribute('data-prefill-' + f.name))); });
        var row = el('div', { 'class': 'te-actions' });
        row.appendChild(el('button', { type: 'submit', 'class': 'te-btn' }, form.getAttribute('data-submit-label') || schema.submitLabel || 'Send'));
        frag.appendChild(row);
        form.appendChild(frag);
        form.removeAttribute('data-te-render');
        init(form);
      })
      .catch(function () {
        form.appendChild(el('p', { 'class': 'te-status te-status--error' }, 'This form could not load. Please email ' + (form.getAttribute('data-fallback-email') || 'help@totalelement.com') + '.'));
      });
  }

  function boot(root) {
    (root || document).querySelectorAll('form[data-te-form]').forEach(function (f) {
      if (f.hasAttribute('data-te-render')) render(f);
      else init(f);
    });
  }

  window.TeForms = { init: init, render: render, boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
